import React, { useMemo } from "react";
import { List as VList } from "react-window";

/**
 * Virtualized data table for Owner Dashboard "Data" tab.
 * Renders all filtered rows in a single scrollable, virtualized list.
 * Sticky header + sticky totals strip. Pre-computes totals over the entire result set.
 */
const COLS = "120px 110px 1fr 90px 80px 80px 100px 120px 70px 110px";
const ROW_H = 36;

const Row = React.memo(function Row({ index, style, rows, c, dark }) {
  const r = rows[index];
  return (
    <div
      data-testid={`owner-data-row-${index}`}
      style={{
        ...style,
        display: "grid",
        gridTemplateColumns: COLS,
        alignItems: "center",
        borderBottom: `1px solid ${c.border}`,
        background: index % 2 === 0 ? "transparent" : (dark ? "rgba(255,255,255,0.015)" : "rgba(0,0,0,0.015)"),
        padding: "0 14px",
        gap: 8,
        fontFamily: "'JetBrains Mono',monospace",
        fontSize: "0.72rem",
        color: c.text,
        whiteSpace: "nowrap",
      }}
    >
      <span>{r.po_number}</span>
      <span>
        <span style={{
          fontFamily: "'Outfit',system-ui,sans-serif",
          fontSize: "0.62rem",
          fontWeight: 500,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          padding: "2px 7px",
          borderRadius: 4,
          color: r.po_status === "Approved" ? c.revenue : r.po_status === "Canceled" ? c.cost : c.sub,
          background: r.po_status === "Approved" ? c.revenueBg : r.po_status === "Canceled" ? c.costBg : "transparent",
          border: `1px solid ${(r.po_status === "Approved" ? c.revenue : r.po_status === "Canceled" ? c.cost : c.border)}22`,
        }}>{r.po_status || "-"}</span>
      </span>
      <span style={{ overflow: "hidden", textOverflow: "ellipsis", fontFamily: "'Outfit',system-ui,sans-serif", fontSize: "0.75rem" }}>{r.main_title || "-"}</span>
      <span style={{ color: c.sub, fontSize: "0.7rem" }}>{r.task || "-"}</span>
      <span style={{ textAlign: "right" }}>{r.qty || r.duration_words || "-"}</span>
      <span style={{ textAlign: "right" }}>{r.client_rate ? `$${r.client_rate}` : "-"}</span>
      <span style={{ textAlign: "right", color: c.profit }}>{r.client_amount ? `$${r.client_amount.toLocaleString()}` : "-"}</span>
      <span style={{ textAlign: "right", color: c.revenue }}>{r.client_amount_inr ? `₹${r.client_amount_inr.toLocaleString("en-IN", { maximumFractionDigits: 2 })}` : "-"}</span>
      <span style={{ textAlign: "right", color: c.orange, fontSize: "0.62rem" }}>{r.exchange_rate ? `₹${r.exchange_rate.toFixed(1)}` : "-"}</span>
      <span style={{ color: c.muted, fontSize: "0.68rem" }}>{r.core_date || r.file_received_on || "-"}</span>
    </div>
  );
});

export default function DataTable({ durData, c, dark, font, mono, fmt, fmtUsd }) {
  const rows = durData?.rows || [];

  // Pre-compute totals over the FULL filtered set, EXCLUDING Cancelled POs (they are not real revenue).
  const totals = useMemo(() => {
    let qty = 0, usd = 0, inr = 0, cancelled = 0;
    for (const r of rows) {
      if (r.po_status === "Canceled") { cancelled++; continue; }
      qty += (r.qty || r.duration_words || 0);
      usd += (r.client_amount || 0);
      inr += (r.client_amount_inr || 0);
    }
    return { qty, usd, inr, cancelled };
  }, [rows]);

  const rowProps = useMemo(() => ({ rows, c, dark }), [rows, c, dark]);

  if (!durData) {
    return <div style={{ padding: 60, textAlign: "center", color: c.muted, fontFamily: font, fontSize: "0.85rem" }}>Loading...</div>;
  }
  if (rows.length === 0) {
    return <div data-testid="owner-data-empty" style={{ padding: 60, textAlign: "center", color: c.muted, fontFamily: font, fontSize: "0.85rem", background: c.surface, border: `1px solid ${c.border}`, borderRadius: 14 }}>No records match the current filters.</div>;
  }

  return (
    <div data-testid="owner-data-table" style={{ background: c.surface, border: `1px solid ${c.border}`, borderRadius: 14, overflow: "hidden" }}>
      {/* Totals strip */}
      <div style={{ display: "flex", alignItems: "center", gap: 20, padding: "12px 16px", borderBottom: `1px solid ${c.border}`, background: dark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)" }}>
        <span data-testid="owner-data-total-label" style={{ fontFamily: font, fontSize: "0.72rem", fontWeight: 600, color: c.text, letterSpacing: "0.06em", textTransform: "uppercase" }}>TOTAL ({rows.length} records{totals.cancelled > 0 ? ` · ${totals.cancelled} cancelled excluded` : ""})</span>
        <span style={{ marginLeft: "auto", fontFamily: mono, fontSize: "0.78rem", color: c.sub }}>QTY <span data-testid="owner-data-total-qty" style={{ fontWeight: 600, color: c.text }}>{totals.qty.toLocaleString()}</span></span>
        <span style={{ fontFamily: mono, fontSize: "0.78rem", color: c.sub }}>USD <span data-testid="owner-data-total-usd" style={{ fontWeight: 600, color: c.profit }}>{fmtUsd(totals.usd)}</span></span>
        <span style={{ fontFamily: mono, fontSize: "0.78rem", color: c.sub }}>INR <span data-testid="owner-data-total-inr" style={{ fontWeight: 600, color: c.revenue }}>{fmt(totals.inr)}</span></span>
      </div>

      {/* Header */}
      <div style={{ display: "grid", gridTemplateColumns: COLS, padding: "10px 14px", gap: 8, borderBottom: `1px solid ${c.border}`, background: c.surface, fontFamily: font, fontSize: "0.58rem", letterSpacing: "0.1em", color: c.muted, fontWeight: 500, textTransform: "uppercase" }}>
        <span>PO</span>
        <span>STATUS</span>
        <span>TITLE</span>
        <span>TASK</span>
        <span style={{ textAlign: "right" }}>QTY</span>
        <span style={{ textAlign: "right" }}>RATE</span>
        <span style={{ textAlign: "right" }}>USD</span>
        <span style={{ textAlign: "right" }}>INR</span>
        <span style={{ textAlign: "right" }}>₹/$</span>
        <span>DUE DATE</span>
      </div>

      {/* Virtualized rows — fixed viewport height, only renders visible rows */}
      <VList
        rowComponent={Row}
        rowCount={rows.length}
        rowHeight={ROW_H}
        rowProps={rowProps}
        overscanCount={8}
        style={{ height: "calc(100vh - 320px)", minHeight: 400 }}
      />
    </div>
  );
}
